import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Storefront, Plus, LockKey, LockKeyOpen, PencilSimple, Trash, Eye, Warning,
  PlusCircle, MinusCircle,
} from "@phosphor-icons/react";

export default function MasterResellers() {
  const [items, setItems] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [customCredits, setCustomCredits] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/resellers");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (r) => {
    const next = r.status === "active" ? "blocked" : "active";
    try {
      await api.patch(`/resellers/${r.id}/status`, { status: next });
      toast.success(next === "blocked" ? "Revendedor suspenso" : "Suspensão removida");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const quickCredits = async (r, delta) => {
    try {
      await api.post(`/resellers/${r.id}/credits`, {
        amount: delta,
        description: delta > 0 ? `+${delta} créditos (rápido)` : `${delta} créditos (rápido)`,
      });
      toast.success(delta > 0 ? `+${delta} créditos` : `${delta} créditos`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const impersonate = async (r) => {
    try {
      const { data } = await api.post(`/master/impersonate/reseller/${r.id}`);
      const url = `/impersonate?token=${encodeURIComponent(data.token)}&redirect=${encodeURIComponent(data.redirect)}`;
      window.open(url, "_blank", "noopener");
      toast.success(`Abrindo painel de ${data.reseller_name} em nova aba`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteReseller = async () => {
    try {
      await api.delete(`/resellers/${deleting.id}`);
      toast.success("Revendedor excluído");
      setDeleting(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Rede</p>
          <h1 className="text-4xl font-black tracking-tighter">Revendedores</h1>
          <p className="text-sm text-[#5C5C59] mt-2">Parceiros white-label que revendem sua plataforma.</p>
        </div>
        <Button onClick={() => setOpenCreate(true)} data-testid="create-reseller-btn"
          className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
          <Plus size={14} weight="bold" className="mr-2" /> Novo revendedor
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E0] shadow-brutal overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E5E0] text-[10px] uppercase tracking-wider text-[#5C5C59]">
              <th className="text-left px-4 py-3">Revendedor</th>
              <th className="text-left px-4 py-3">E-mail dono</th>
              <th className="text-center px-4 py-3">Créditos</th>
              <th className="text-right px-4 py-3">Empresas</th>
              <th className="text-right px-4 py-3">Comissão</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3 min-w-[300px]">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-6 text-[#5C5C59]">Carregando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-[#5C5C59]">
                <Storefront size={28} className="mx-auto mb-2 opacity-40" />
                Nenhum revendedor ainda. Crie o primeiro para começar.
              </td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} data-testid={`reseller-row-${r.id}`} className="border-b border-[#F0F0EC] hover:bg-[#F7F7F5]">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-full" style={{ background: r.brand_color || "#0F4C3A" }} />
                    <span className="font-semibold">{r.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-[#5C5C59] font-mono text-xs">{r.owner_email}</td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => quickCredits(r, -10)}
                      data-testid={`credit-minus-${r.id}`}
                      title="Remover 10 créditos"
                      className="p-1 border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white transition-colors"
                    >
                      <MinusCircle size={14} weight="bold" />
                    </button>
                    <span
                      className="font-mono font-bold min-w-[3ch] text-center px-2 cursor-pointer hover:underline decoration-dotted"
                      style={{ color: r.credit_balance < 0 ? "#DC2626" : "#0F4C3A" }}
                      data-testid={`credit-balance-${r.id}`}
                      title="Clique para ajuste customizado"
                      onClick={() => setCustomCredits(r)}
                    >
                      {r.credit_balance}
                    </span>
                    <button
                      onClick={() => quickCredits(r, 10)}
                      data-testid={`credit-plus-${r.id}`}
                      title="Adicionar 10 créditos"
                      className="p-1 border border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white transition-colors"
                    >
                      <PlusCircle size={14} weight="bold" />
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">{r.companies_count}</td>
                <td className="px-4 py-3 text-right text-xs">{r.commission_rate?.toFixed(1)}%</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${r.status === "active" ? "bg-[#EDF3F0] text-[#0F4C3A]" : "bg-[#FEE2E2] text-[#DC2626]"}`}>
                    {r.status === "active" ? "Ativo" : "Suspenso"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1 flex-wrap">
                    <IconBtn icon={Eye} label="Entrar como" testid={`impersonate-${r.id}`} onClick={() => impersonate(r)} variant="primary" />
                    <IconBtn icon={PencilSimple} label="Editar" testid={`edit-${r.id}`} onClick={() => setEditing(r)} />
                    <IconBtn
                      icon={r.status === "active" ? LockKey : LockKeyOpen}
                      label={r.status === "active" ? "Suspender" : "Liberar"}
                      testid={`toggle-status-${r.id}`}
                      onClick={() => toggleStatus(r)}
                      variant={r.status === "active" ? "warn" : "success"}
                    />
                    <IconBtn icon={Trash} label="Excluir" testid={`delete-${r.id}`} onClick={() => setDeleting(r)} variant="danger" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateResellerDialog open={openCreate} onOpenChange={setOpenCreate} onDone={load} />
      {editing && <EditResellerDialog reseller={editing} onClose={() => setEditing(null)} onDone={load} />}
      {customCredits && (
        <CustomCreditsDialog
          reseller={customCredits}
          onClose={() => setCustomCredits(null)}
          onDone={load}
        />
      )}
      {deleting && (
        <Dialog open onOpenChange={(o) => !o && setDeleting(null)}>
          <DialogContent className="bg-white rounded-none border-2 border-[#DC2626] max-w-md" data-testid="delete-confirm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#DC2626]">
                <Warning size={20} weight="fill" /> Excluir revendedor
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm">
              Isso vai remover <b>{deleting.name}</b>, todos os planos, transações, saques e o usuário dono. <b>Não dá pra desfazer.</b>
              {deleting.companies_count > 0 && (
                <div className="mt-3 p-3 bg-[#FEF3C7] border-l-[3px] border-[#D97706] text-xs">
                  ⚠️ Este revendedor tem <b>{deleting.companies_count}</b> empresa(s). Exclua/transfira as empresas antes.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleting(null)} className="rounded-none">Cancelar</Button>
              <Button onClick={deleteReseller} data-testid="confirm-delete-btn" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-none">
                Sim, excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Kept for compatibility if legacy code opens credits dialog directly */}
    </div>
  );
}

function CustomCreditsDialog({ reseller, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const n = parseInt(amount, 10);
  const invalid = !Number.isFinite(n) || n === 0;
  const newBalance = invalid ? reseller.credit_balance : reseller.credit_balance + n;

  const apply = async () => {
    if (invalid) return toast.error("Digite um número diferente de zero");
    setSaving(true);
    try {
      await api.post(`/resellers/${reseller.id}/credits`, {
        amount: n,
        description: description.trim() || (n > 0 ? `+${n} créditos manuais` : `${n} créditos manuais`),
      });
      toast.success(n > 0 ? `+${n} créditos aplicados` : `${n} créditos aplicados`);
      onClose();
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-none max-w-md" data-testid="custom-credits-dialog">
        <DialogHeader>
          <DialogTitle>Ajustar créditos — {reseller.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-[#F7F7F5] border-l-[3px] border-[#5C5C59]">
              <div className="text-[10px] uppercase tracking-wider text-[#5C5C59]">Saldo atual</div>
              <div className="text-2xl font-black font-mono">{reseller.credit_balance}</div>
            </div>
            <div className={`p-3 border-l-[3px] ${invalid ? "bg-[#F7F7F5] border-[#5C5C59]" : (newBalance < 0 ? "bg-[#FEE2E2] border-[#DC2626]" : "bg-[#EDF3F0] border-[#0F4C3A]")}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#5C5C59]">Após ajuste</div>
              <div className={`text-2xl font-black font-mono ${newBalance < 0 ? "text-[#DC2626]" : "text-[#0F4C3A]"}`}>{invalid ? "—" : newBalance}</div>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Ajuste (positivo credita, negativo debita)</Label>
            <Input
              data-testid="custom-credits-amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-none mt-1 font-mono text-lg"
              placeholder="Ex.: 50 ou -25"
              autoFocus
            />
            <div className="flex gap-1 mt-2">
              {[10, 25, 50, 100, 500].map((q) => (
                <button
                  key={q}
                  onClick={() => setAmount(String(q))}
                  className="text-[10px] px-2 py-1 border border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white"
                >
                  +{q}
                </button>
              ))}
              {[-10, -25, -50, -100].map((q) => (
                <button
                  key={q}
                  onClick={() => setAmount(String(q))}
                  className="text-[10px] px-2 py-1 border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Descrição (opcional)</Label>
            <Input
              data-testid="custom-credits-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-none mt-1"
              placeholder="Ex.: Bônus de boas-vindas, estorno, etc."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancelar</Button>
          <Button
            onClick={apply}
            disabled={saving || invalid}
            data-testid="custom-credits-apply"
            className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none disabled:opacity-40"
          >
            {saving ? "Aplicando…" : "Aplicar ajuste"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IconBtn({ icon: Icon, label, onClick, testid, variant }) {
  const cls = variant === "danger"
    ? "border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
    : variant === "warn"
      ? "border-[#D97706] text-[#D97706] hover:bg-[#D97706] hover:text-white"
      : variant === "success"
        ? "border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white"
        : variant === "primary"
          ? "border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white"
          : "border-[#E5E5E0] text-[#5C5C59] hover:border-[#0F4C3A] hover:text-[#0F4C3A]";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={label}
      className={`inline-flex items-center gap-1 text-[10px] px-2 py-1.5 border transition-colors ${cls}`}
    >
      <Icon size={12} weight="bold" />
      <span className="hidden xl:inline">{label}</span>
    </button>
  );
}

function CreateResellerDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ name: "", owner_email: "", owner_name: "", owner_password: "", brand_color: "#0F4C3A", commission_rate: 40 });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/resellers", { ...form, commission_rate: Number(form.commission_rate) });
      toast.success("Revendedor criado");
      onOpenChange(false);
      setForm({ name: "", owner_email: "", owner_name: "", owner_password: "", brand_color: "#0F4C3A", commission_rate: 40 });
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-lg" data-testid="create-reseller-dialog">
        <DialogHeader><DialogTitle>Novo revendedor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Nome da revenda</Label>
            <Input data-testid="reseller-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Nome do dono</Label>
              <Input data-testid="reseller-owner-name-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">E-mail</Label>
              <Input data-testid="reseller-owner-email-input" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="rounded-none mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Senha</Label>
              <Input data-testid="reseller-owner-password-input" type="password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Comissão (%)</Label>
              <Input type="number" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} className="rounded-none mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Cor da marca</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="rounded-none w-14 h-10 p-1" />
              <Input value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="rounded-none flex-1 font-mono" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button data-testid="submit-create-reseller" onClick={submit} disabled={loading} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
            {loading ? "Criando…" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditResellerDialog({ reseller, onClose, onDone }) {
  const [form, setForm] = useState({
    name: reseller.name || "",
    brand_color: reseller.brand_color || "#0F4C3A",
    commission_rate: reseller.commission_rate ?? 40,
    slug: reseller.slug || "",
  });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.patch(`/resellers/${reseller.id}`, {
        name: form.name,
        brand_color: form.brand_color,
        commission_rate: Number(form.commission_rate),
        slug: form.slug,
      });
      toast.success("Revendedor atualizado");
      onClose();
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-none max-w-lg" data-testid="edit-reseller-dialog">
        <DialogHeader><DialogTitle>Editar revendedor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Nome</Label>
            <Input data-testid="edit-reseller-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Slug (URL /r/xxx)</Label>
              <Input data-testid="edit-reseller-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="rounded-none mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Comissão (%)</Label>
              <Input data-testid="edit-reseller-commission" type="number" min="0" max="100" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} className="rounded-none mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Cor da marca</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="rounded-none w-14 h-10 p-1" />
              <Input value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="rounded-none flex-1 font-mono" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancelar</Button>
          <Button data-testid="edit-reseller-save" onClick={submit} disabled={loading} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
            {loading ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
